import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import '../Analitos.scss';
import Popup from 'devextreme-react/popup';
import DataGrid, { Column, Editing, Item, Toolbar, Lookup, RequiredRule, NumericRule, LoadPanel, Button as GridButton } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { useApiConfiguration } from '../../../api/api.configuration';
import { useAppSelector } from '../../../contexts/hooks';
import { useScreenSize } from '../../../utils/media-query';
import { newToast } from '../../../components';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { RangoMasivo, SysIAnalitosRangosExtendidos } from '../../../types/types.analitos';

interface ModalRangosExtendidosProps {
  visible: boolean;
  setVisible: (value: boolean) => void;
  analitoId: number;
  analitoNombre: string;
  onSave?: () => void;
}

export const ModalRangosExtendidos = ({ visible, setVisible, analitoId, analitoNombre, onSave }: ModalRangosExtendidosProps) => {
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const { getRangosExtendidos, createRangosExtendidos, updateRangosExtendidos, deleteRangoExtendido } = useApiConfiguration();

  // Obtener géneros desde useAppSelector
  const generosData = useAppSelector((x) => x.configuration.Data?.ComboBox?.find((combo: any) => combo.Name === 'SysI_Genero')?.Data || []);

  const [loading, setLoading] = useState(false);
  const [showMassiveCreation, setShowMassiveCreation] = useState(false);
  const [rangosMasivos, setRangosMasivos] = useState<RangoMasivo[]>([]);
  const dataGridRef = useRef<DataGrid>(null);
  const gridRef = useRef<DataGrid>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (visible && analitoId > 0) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [visible, analitoId]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setRangosMasivos([]);
    setShowMassiveCreation(false);
  }, [setVisible]);

  const handleCloseMassive = useCallback(() => {
    setShowMassiveCreation(false);
    setRangosMasivos([]);
  }, []);

  // Función para convertir días a años (solo para mostrar)
  const diasAAnos = useCallback((dias: number): string => {
    if (dias >= 365) {
      const anos = Math.floor(dias / 365);
      const diasRestantes = dias % 365;
      if (diasRestantes > 0) {
        return `${anos} año${anos > 1 ? 's' : ''} ${diasRestantes} día${diasRestantes > 1 ? 's' : ''}`;
      }
      return `${anos} año${anos > 1 ? 's' : ''}`;
    }
    return `${dias} día${dias > 1 ? 's' : ''}`;
  }, []);

  // Función para crear rangos masivos predefinidos consecutivos
  const crearRangosPredefinidos = () => {
    const rangosBase: RangoMasivo[] = [];

    // Para cada género disponible
    generosData.forEach((genero: any) => {
      // Rangos típicos por edad en DÍAS - Consecutivos automáticamente
      const rangosTipicos = [
        { minDias: 0, maxDias: 30 }, // Neonatos: 0-30 días
        { minDias: 31, maxDias: 365 }, // Lactantes: 31 días - 1 año
        { minDias: 366, maxDias: 1825 }, // Niños: 1-5 años (366-1825 días)
        { minDias: 1826, maxDias: 4380 }, // Niños: 5-12 años (1826-4380 días)
        { minDias: 4381, maxDias: 6570 }, // Adolescentes: 12-18 años (4381-6570 días)
        { minDias: 6571, maxDias: 23725 }, // Adultos: 18-65 años (6571-23725 días)
        { minDias: 23726, maxDias: 43800 }, // Adultos mayores: 65-120 años (23726-43800 días)
      ];

      rangosTipicos.forEach((rango, index) => {
        rangosBase.push({
          genero: genero.Gn_Id,
          edadMinima: rango.minDias,
          edadMaxima: rango.maxDias,
          valorMin: 0,
          valorMax: 100,
          estado: true,
        });
      });
    });

    setRangosMasivos(rangosBase);
  };

  // Función para guardar rangos masivos
  const guardarRangosMasivos = async () => {
    if (rangosMasivos.length === 0) {
      newToast('WARNING', 'No hay rangos para guardar', 3);
      return;
    }

    if (!analitoId || analitoId <= 0) {
      newToast('ERROR', 'ID de analito inválido', 3);
      return;
    }

    try {
      setLoading(true);

      // Convertir a formato del backend
      const rangosParaBackend: SysIAnalitosRangosExtendidos[] = rangosMasivos.map((rango) => ({
        Rex_Id: 0,
        Rex_An_Id: analitoId,
        Rex_Gn_Id: rango.genero,
        Rex_EdadMinima: rango.edadMinima,
        Rex_EdadMaxima: rango.edadMaxima,
        Rex_ValRefMin: rango.valorMin,
        Rex_ValRefMax: rango.valorMax,
        Rex_Estado: rango.estado,
      }));

      await createRangosExtendidos(analitoId, rangosParaBackend);

      newToast('SUCCESS', `Se crearon ${rangosMasivos.length} rangos extendidos exitosamente`, 3);

      // Cerrar modal masivo y refrescar grid
      setShowMassiveCreation(false);
      setRangosMasivos([]);

      setTimeout(() => {
        if (dataGridRef.current) {
          dataGridRef.current.instance.refresh();
        }
      }, 100);
    } catch (error) {
      console.error('Error guardando rangos masivos:', error);
      newToast('ERROR', 'Error guardando los rangos masivos', 3);
    } finally {
      setLoading(false);
    }
  };

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Rex_Id',
      load: async () => {
        if (!isModalOpen || !analitoId || analitoId <= 0) {
          return { data: [], totalCount: 0 };
        }

        try {
          const data = await getRangosExtendidos(analitoId);

          // Los datos ya vienen en días, no necesitamos procesamiento adicional
          return {
            data: data || [],
            totalCount: (data || []).length,
          };
        } catch (error) {
          newToast('ERROR', 'Error cargando los rangos extendidos', 3);
          return { data: [], totalCount: 0 };
        }
      },
      insert: async (values: any) => {
        if (!analitoId || analitoId <= 0) {
          throw new Error('ID de analito inválido');
        }

        try {
          const newRango = {
            Rex_Gn_Id: values.Rex_Gn_Id,
            Rex_EdadMinima: values.Rex_EdadMinima,
            Rex_EdadMaxima: values.Rex_EdadMaxima,
            Rex_ValRefMin: values.Rex_ValRefMin,
            Rex_ValRefMax: values.Rex_ValRefMax,
            Rex_An_Id: analitoId,
            Rex_Estado: values.Rex_Estado !== undefined ? values.Rex_Estado : true,
          };

          const result = await createRangosExtendidos(analitoId, [newRango]);

          if (dataGridRef.current) {
            dataGridRef.current.instance.refresh();
          }

          return result && result.length > 0 ? result[0] : newRango;
        } catch (error) {
          throw new Error('Error creando el rango extendido');
        }
      },
      update: async (key: any, values: any) => {
        return Promise.resolve(values);
      },
      remove: async (key: any) => {
        try {
          const success = await deleteRangoExtendido(key);
          throw new Error('No se pudo eliminar el rango');
        } catch (error) {
          newToast('ERROR', 'Error eliminando el rango extendido', 3);
          return;
        }
      },
    });
  }, [analitoId, isModalOpen]);

  const customDataSrc = useMemo(() => {
    return new DataSource({
      store: customData,
      reshapeOnPush: true,
    });
  }, [customData]);

  // Función para manejar el cierre y ejecutar onSave solo entonces
  const handleCloseAndSave = useCallback(() => {
    onSave?.();
    handleClose();
  }, [onSave, handleClose]);

  // Función para validar rangos consecutivos
  const validarRangosConsecutivos = useCallback(async (newData: any, oldData: any) => {
    const data = { ...oldData, ...newData };
    const errors: string[] = [];

    // Validaciones básicas
    if (data.Rex_EdadMinima !== undefined && data.Rex_EdadMinima < 0) {
      errors.push('La edad mínima no puede ser negativa');
    }
    if (data.Rex_EdadMaxima !== undefined && data.Rex_EdadMaxima < 0) {
      errors.push('La edad máxima no puede ser negativa');
    }
    if (data.Rex_EdadMinima !== undefined && data.Rex_EdadMaxima !== undefined && data.Rex_EdadMinima >= data.Rex_EdadMaxima) {
      errors.push('La edad mínima debe ser menor que la edad máxima');
    }
    if (data.Rex_ValRefMin !== undefined && data.Rex_ValRefMax !== undefined && data.Rex_ValRefMin >= data.Rex_ValRefMax) {
      errors.push('El valor mínimo debe ser menor que el valor máximo');
    }

    return errors;
  }, []);

  const onRowValidating = useCallback(
    async (e: any) => {
      const errors = await validarRangosConsecutivos(e.newData, e.oldData);
      if (errors.length > 0) {
        e.isValid = false;
        e.errorText = errors.join('; ');
      }
    },
    [validarRangosConsecutivos]
  );

  // CustomStore para rangos masivos
  const rangosMasivosDataSource = useMemo(() => {
    return new DataSource({
      store: new CustomStore({
        key: 'tempId',
        load: () => {
          return Promise.resolve(
            rangosMasivos.map((rango, index) => ({
              tempId: index,
              ...rango,
            }))
          );
        },
        insert: async (values) => {
          const newRango: RangoMasivo = {
            genero: values.genero || generosData[0]?.Gn_Id || 1,
            edadMinima: values.edadMinima || 0,
            edadMaxima: values.edadMaxima || 30,
            valorMin: values.valorMin || 0,
            valorMax: values.valorMax || 100,
            estado: values.estado !== undefined ? values.estado : true,
          };

          setRangosMasivos((prev) => [...prev, newRango]);
          return Promise.resolve({ tempId: rangosMasivos.length, ...newRango });
        },
        update: async (key, values) => {
          setRangosMasivos((prev) => prev.map((rango, index) => (index === key ? { ...rango, ...values } : rango)));
          const updatedRango = { ...rangosMasivos[key], ...values };
          return Promise.resolve({ RexId: key, ...updatedRango });
        },
        remove: async (key) => {
          setRangosMasivos((prev) => prev.filter((_, index) => index !== key));
          return Promise.resolve();
        },
      }),
      reshapeOnPush: false,
    });
  }, [rangosMasivos, generosData]);

  return (
    <>
      {/* Modal Principal */}
      <Popup
        width={1100}
        height={600}
        fullScreen={isMedium || isSmall || isXSmall}
        visible={visible}
        onHidden={() => {
          if (!showMassiveCreation) {
            handleCloseAndSave();
          }
        }}
        title={`RANGOS EXTENDIDOS: ${analitoId} - ${analitoNombre}`}
        showTitle={true}
        showCloseButton={true}
        resizeEnabled={true}
        style={{ display: showMassiveCreation ? 'none' : 'block' }}
        contentComponent={() => (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            <div style={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                ref={dataGridRef}
                dataSource={customDataSrc}
                showBorders={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                hoverStateEnabled={true}
                onRowUpdating={async (e) => {
                  try {
                    const mergedData = { ...e.oldData, ...e.newData };

                    const updatedRango = {
                      Rex_Id: e.key,
                      Rex_Gn_Id: mergedData.Rex_Gn_Id,
                      Rex_EdadMinima: mergedData.Rex_EdadMinima,
                      Rex_EdadMaxima: mergedData.Rex_EdadMaxima,
                      Rex_ValRefMin: mergedData.Rex_ValRefMin,
                      Rex_ValRefMax: mergedData.Rex_ValRefMax,
                      Rex_An_Id: analitoId,
                      Rex_Estado: mergedData.Rex_Estado !== undefined ? mergedData.Rex_Estado : true,
                    };

                    const result = await updateRangosExtendidos([updatedRango]);

                    if (result && result.length > 0) {
                      e.newData = { ...mergedData, ...result[0] };
                    }
                  } catch (error) {
                    e.cancel = true;
                    newToast('ERROR', 'Error actualizando el rango extendido', 3);
                  }
                }}
                onRowUpdated={(e) => {
                  setTimeout(() => {
                    if (dataGridRef.current) {
                      dataGridRef.current.instance.refresh();
                    }
                  }, 500);
                }}
                onRowValidating={onRowValidating}
                height='100%'
                width='100%'
                columnAutoWidth={false}
                repaintChangesOnly={true}
                cacheEnabled={true}>
                <LoadPanel enabled={loading} />

                <Editing
                  mode='row'
                  allowAdding={true}
                  allowUpdating={true}
                  allowDeleting={true}
                  useIcons={true}
                  selectTextOnEditStart={true}
                  texts={{
                    saveRowChanges: 'Guardar',
                    cancelRowChanges: 'Cancelar',
                    deleteRow: 'Eliminar',
                    addRow: 'Agregar Rango',
                  }}
                />

                <Toolbar>
                  <Item
                    location='before'
                    template={() => (
                      <div className='grid-header' style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Rangos por Edad y Género
                      </div>
                    )}
                  />
                  <Item name='addRowButton' />
                  <Item location='after'>
                    <Button text='Creación Masiva' type='default' stylingMode='contained' onClick={() => setShowMassiveCreation(true)} hint='Crear múltiples rangos a la vez' />
                  </Item>
                </Toolbar>

                <Column dataField='Rex_Gn_Id' caption='GÉNERO' width={120} alignment='center'>
                  <Lookup dataSource={generosData} displayExpr='Gn_Nombre' valueExpr='Gn_Id' />
                  <RequiredRule message='El género es requerido' />
                </Column>

                <Column dataField='Rex_EdadMinima' caption='EDAD MÍNIMA (DÍAS)' dataType='number' width={140} alignment='center'>
                  <RequiredRule message='La edad mínima es requerida' />
                  <NumericRule message='Debe ser un número válido mayor o igual a 0' ignoreEmptyValue={false} />
                </Column>

                <Column
                  caption='EDAD MÍN. (REFERENCIA)'
                  width={160}
                  allowEditing={false}
                  alignment='center'
                  calculateCellValue={(rowData) => {
                    const dias = rowData.Rex_EdadMinima || 0;
                    return diasAAnos(dias);
                  }}
                  cellRender={(cellData) => {
                    return <span style={{ color: '#666', fontStyle: 'italic', fontSize: '12px' }}>{cellData.value}</span>;
                  }}
                />

                <Column dataField='Rex_EdadMaxima' caption='EDAD MÁXIMA (DÍAS)' dataType='number' width={140} alignment='center'>
                  <RequiredRule message='La edad máxima es requerida' />
                  <NumericRule message='Debe ser un número válido' ignoreEmptyValue={false} />
                </Column>

                <Column
                  caption='EDAD MÁX. (REFERENCIA)'
                  width={160}
                  allowEditing={false}
                  alignment='center'
                  calculateCellValue={(rowData) => {
                    const dias = rowData.Rex_EdadMaxima || 0;
                    return diasAAnos(dias);
                  }}
                  cellRender={(cellData) => {
                    return <span style={{ color: '#666', fontStyle: 'italic', fontSize: '12px' }}>{cellData.value}</span>;
                  }}
                />

                <Column dataField='Rex_ValRefMin' caption='VALOR MÍN.' dataType='number' width={100} format='#0.##' alignment='center'>
                  <RequiredRule message='El valor mínimo es requerido' />
                  <NumericRule message='Debe ser un número válido' ignoreEmptyValue={false} />
                </Column>

                <Column dataField='Rex_ValRefMax' caption='VALOR MÁX.' dataType='number' width={100} format='#0.##' alignment='center'>
                  <RequiredRule message='El valor máximo es requerido' />
                  <NumericRule message='Debe ser un número válido' ignoreEmptyValue={false} />
                </Column>

                <Column dataField='Rex_Estado' caption='ESTADO' dataType='boolean' width={80} alignment='center' />
              </DataGrid>
            </div>

            <div
              style={{
                padding: '15px 20px',
                textAlign: 'center',
                borderTop: '1px solid #ddd',
                backgroundColor: '#f8f9fa',
                flexShrink: 0,
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <strong>Nota:</strong> Los rangos se almacenan en días. Las columnas de referencia muestran la equivalencia aproximada en años cuando aplica. Los rangos deben ser consecutivos (el día siguiente debe ser un día mayor al día final del rango anterior).
              </div>
            </div>
          </div>
        )}
      />

      {/* Modal de Creación Masiva */}
      {showMassiveCreation && (
        <Popup
          width={1100}
          height={700}
          fullScreen={isMedium || isSmall}
          visible={true}
          onHidden={() => {
            handleCloseMassive();
          }}
          title={`CREACIÓN MASIVA RANGOS EXTENDIDOS: ${analitoId} - ${analitoNombre}`}
          showTitle={true}
          showCloseButton={true}
          resizeEnabled={true}
          contentComponent={() => (
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}>
              {/* Toolbar de acciones masivas */}
              <div
                style={{
                  padding: '15px 20px',
                  borderBottom: '1px solid #ddd',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}>
                <Button text='Cargar Rangos Típicos' type='default' onClick={crearRangosPredefinidos} hint='Carga rangos típicos por edad para todos los géneros' />
                <Button text='Limpiar Todo' type='normal' onClick={() => setRangosMasivos([])} />

                <div style={{ flex: 1 }} />

                <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>Rangos a crear: {rangosMasivos.length}</div>
              </div>

              {/* DataGrid para edición masiva */}
              <div style={{ flex: 1, minHeight: 0 }}>
                <DataGrid dataSource={rangosMasivosDataSource} showBorders={true} showRowLines={true} rowAlternationEnabled={true} hoverStateEnabled={true} height='100%' width='100%' columnAutoWidth={false}>
                  <Editing mode='cell' allowAdding={true} allowUpdating={true} allowDeleting={true} />
                  <Toolbar>
                    <Item name='addRowButton' />
                  </Toolbar>

                  <Column dataField='genero' caption='GÉNERO' width={100} alignment='center'>
                    <Lookup dataSource={generosData} displayExpr='Gn_Nombre' valueExpr='Gn_Id' />
                    <RequiredRule message='El género es requerido' />
                  </Column>

                  <Column dataField='edadMinima' caption='EDAD MÍN. (DÍAS)' dataType='number' width={120} alignment='center'>
                    <RequiredRule message='La edad mínima es requerida' />
                    <NumericRule message='Debe ser un número válido mayor o igual a 0' ignoreEmptyValue={false} />
                  </Column>

                  <Column
                    caption='EDAD MÍN. (REF.)'
                    width={140}
                    allowEditing={false}
                    alignment='center'
                    calculateCellValue={(rowData) => {
                      const dias = rowData.edadMinima || 0;
                      return diasAAnos(dias);
                    }}
                    cellRender={(cellData) => {
                      return <span style={{ color: '#666', fontStyle: 'italic', fontSize: '11px' }}>{cellData.value}</span>;
                    }}
                  />

                  <Column dataField='edadMaxima' caption='EDAD MÁX. (DÍAS)' dataType='number' width={120} alignment='center'>
                    <RequiredRule message='La edad máxima es requerida' />
                    <NumericRule message='Debe ser un número válido' ignoreEmptyValue={false} />
                  </Column>

                  <Column
                    caption='EDAD MÁX. (REF.)'
                    width={140}
                    allowEditing={false}
                    alignment='center'
                    calculateCellValue={(rowData) => {
                      const dias = rowData.edadMaxima || 0;
                      return diasAAnos(dias);
                    }}
                    cellRender={(cellData) => {
                      return <span style={{ color: '#666', fontStyle: 'italic', fontSize: '11px' }}>{cellData.value}</span>;
                    }}
                  />

                  <Column dataField='valorMin' caption='VALOR MÍN.' dataType='number' width={90} format='#0.##' alignment='center'>
                    <RequiredRule message='El valor mínimo es requerido' />
                    <NumericRule message='Debe ser un número válido' ignoreEmptyValue={false} />
                  </Column>

                  <Column dataField='valorMax' caption='VALOR MÁX.' dataType='number' width={90} format='#0.##' alignment='center'>
                    <RequiredRule message='El valor máximo es requerido' />
                    <NumericRule message='Debe ser un número válido' ignoreEmptyValue={false} />
                  </Column>

                  <Column dataField='estado' caption='ESTADO' dataType='boolean' width={80} alignment='center' />
                </DataGrid>
              </div>

              {/* Footer con botones de acción */}
              <div
                style={{
                  padding: '15px 20px',
                  borderTop: '1px solid #ddd',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}>
                <Button text='Cancelar' type='normal' onClick={handleCloseMassive} disabled={loading} />
                <Button text={`Guardar ${rangosMasivos.length} Rangos`} type='default' disabled={rangosMasivos.length === 0 || loading} onClick={guardarRangosMasivos} />
              </div>
            </div>
          )}
        />
      )}
    </>
  );
};
